/**
 * Site Layers Block (LK pack) — renders KMZ-imported site geometry as a
 * priority prompt block prepended ABOVE the SED text so the LLM trusts
 * on-site layers over regional external data (HIFLD / PeeringDB / NREL /
 * NWI / FEMA).
 *
 * Background:
 *   `site.siteLayers` (fiber, transmission, pipeline, parcel, constraint
 *   geometry, …) is captured when a user imports a KMZ file. Until this
 *   module landed it was consumed ONLY by `kmzExporter.ts` — the LLM
 *   never saw it. The most visible blast-radius example was the Redhead
 *   feasibility report saying "fiber over 200km away" while the user had
 *   imported a KMZ with a fiber line crossing the parcel.
 *
 * Trust hierarchy:
 *   On-site KMZ geometry is user-curated ground truth specific to THIS
 *   parcel. It supersedes regional shapefiles (HIFLD, PeeringDB, NWI,
 *   FEMA) which are inherently coarse at parcel scale. The block
 *   prepends ABOVE the SED text with an explicit override directive.
 *
 * Why a separate file:
 *   - `reportPrompts.ts` stays focused on per-type narrative templates.
 *   - `siteEvidence.ts` stays focused on API-collected evidence (its
 *     provenance model is `source/confidence`, not user-curated).
 *   - Layers are a third trust tier and earn their own module.
 *
 * Geometry conventions (per CLAUDE.md unit-consistency rule):
 *   - All angles in degrees in the public API, converted to radians at
 *     the boundary via `DEG_TO_RAD`.
 *   - All distances in metres internally; rendered as metres or km in
 *     the output string with a 1_000 m crossover.
 *   - All areas in m² internally; rendered as acres or m² in the output
 *     string with a 0.5-acre crossover (≈2_023 m²).
 *
 * Formulae used:
 *   - Great-circle distance: Haversine (mean Earth radius 6_371_000 m).
 *   - Polyline length: cumulative haversine over consecutive vertices.
 *   - Polygon area: signed spherical-excess shoelace
 *     `A = R² · |Σ (λ₂ − λ₁) · (2 + sin φ₁ + sin φ₂)| / 2`
 *     — accurate to ~0.1 % at parcel scale; we don't need cadastral
 *     precision and the input is itself ±metre-scale from the KMZ.
 */
import type { SiteLayer, SiteLayerCategory } from '../types/site';
import { unionPolygonAreaAcres } from '../lib/siteBoundaryGeometry';
import { partitionSiteLayersForEvidence } from './siteLayers/evidencePolicy';
import { validatedReviewedLandGateLayers } from '../lib/siteLayers/landGatePromotion';

/* ─── Geometry constants (named, no magic numbers — per CLAUDE.md) ─── */

/** Mean Earth radius (WGS-84 mean radius, IUGG 1980) — metres. */
const EARTH_RADIUS_M = 6_371_000;

/** Degrees → radians conversion. */
const DEG_TO_RAD = Math.PI / 180;

/** Metres → kilometres. */
const M_PER_KM = 1_000;

/** Metres² → acres (1 acre = 4_046.8564224 m²). */
const M2_PER_ACRE = 4_046.8564224;

/** Output crossover: render in metres below this, km above. */
const M_TO_KM_THRESHOLD = 1_000;

/** Output crossover: render in m² below this, acres above (~0.5 ac). */
const M2_TO_ACRE_THRESHOLD = 0.5 * M2_PER_ACRE;

/**
 * Polyline "on-parcel" tolerance in metres. A polyline whose nearest
 * vertex is within this distance from the site centroid is annotated as
 * "crosses or abuts the parcel" — a parcel-scale heuristic, not a
 * cadastral assertion.
 */
const ON_PARCEL_TOLERANCE_M = 500;

/**
 * Defensive cap on the rendered block size. The legacy `sedForAnalysis`
 * truncation in `siteRagService` cuts the SED at 60K chars treating it
 * as a flat string. The layers block is prepended ABOVE the SED — so as
 * long as the layers block stays well below 60K we never lose SED
 * content to the truncation. A typical block is 0.5-2K; pathological
 * (>300 layers) would breach the cap and we hard-truncate.
 */
const MAX_BLOCK_CHARS = 4_000;

/* ─── Geometry helpers ─────────────────────────────────────────────── */

/**
 * Great-circle distance between two lat/lng points using the Haversine
 * formula. Returns metres.
 *
 * Inputs: lat/lng in degrees. Output: metres.
 *
 * Exported so the MV pack's distance-chip Haversine fallback can reuse
 * the same numerically-stable implementation (clamped sqrt argument
 * before asin) without forking a second copy.
 */
export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const dLat = (b.lat - a.lat) * DEG_TO_RAD;
  const dLng = (b.lng - a.lng) * DEG_TO_RAD;
  const lat1 = a.lat * DEG_TO_RAD;
  const lat2 = b.lat * DEG_TO_RAD;
  // Numerically stable form: clamp sqrt argument to [0,1] before asin
  // to avoid NaN from floating-point overshoot at very small distances.
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Cumulative length of a polyline in metres — sum of consecutive
 * haversine segments.
 *
 * Inputs: vertices in degrees. Output: metres.
 */
function polylineLengthMeters(
  coords: ReadonlyArray<{ lat: number; lng: number }>,
): number {
  if (coords.length < 2) return 0;
  let sum = 0;
  for (let i = 1; i < coords.length; i++) {
    sum += haversineMeters(coords[i - 1], coords[i]);
  }
  return sum;
}

/**
 * Polygon area in m² using the spherical-excess shoelace approximation:
 *   A = R² · |Σ (λ₂ − λ₁) · (2 + sin φ₁ + sin φ₂)| / 2
 *
 * The ring is treated as closed: if the first and last vertex aren't
 * coincident the closing segment is included implicitly by iterating
 * over all consecutive pairs and wrapping. Sign of the shoelace term
 * indicates winding order — we take `abs` because callers don't care
 * about CW vs CCW for area reporting.
 *
 * Inputs: ring vertices in degrees. Output: metres squared.
 *
 * Accuracy: ~0.1 % at parcel scale; the input itself is ±metre-scale
 * from KMZ so additional precision would be illusory.
 */
function polygonAreaSquareMeters(
  ring: ReadonlyArray<{ lat: number; lng: number }>,
): number {
  if (ring.length < 3) return 0;
  let acc = 0;
  // Iterate over consecutive pairs INCLUDING the wrap (last → first) so
  // the polygon is treated as closed even if the source doesn't
  // duplicate the first vertex.
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    acc +=
      (b.lng - a.lng) * DEG_TO_RAD *
      (2 + Math.sin(a.lat * DEG_TO_RAD) + Math.sin(b.lat * DEG_TO_RAD));
  }
  return Math.abs((acc * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

/**
 * Perimeter of a polygon in metres — sum of haversine segments around
 * the ring (closes the wrap implicitly).
 *
 * Inputs: ring vertices in degrees. Output: metres.
 */
function polygonPerimeterMeters(
  ring: ReadonlyArray<{ lat: number; lng: number }>,
): number {
  if (ring.length < 2) return 0;
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    sum += haversineMeters(a, b);
  }
  return sum;
}

/**
 * Centroid of a polygon ring as the arithmetic mean of vertices. This
 * is a parcel-scale approximation (true geometric centroid would weight
 * each segment by length), but for a roughly-convex parcel the error is
 * well under our M_TO_KM_THRESHOLD output resolution.
 */
function polygonVertexCentroid(
  ring: ReadonlyArray<{ lat: number; lng: number }>,
): { lat: number; lng: number } | null {
  if (ring.length === 0) return null;
  let lat = 0;
  let lng = 0;
  for (const p of ring) {
    lat += p.lat;
    lng += p.lng;
  }
  return { lat: lat / ring.length, lng: lng / ring.length };
}

/**
 * Shortest haversine distance in metres from a point to any vertex of a
 * polyline. This is an O(N) approximation — true point-to-segment
 * distance would project onto each segment — but for the on-parcel
 * heuristic (500 m tolerance) the vertex approximation is sufficient
 * and avoids the segment-projection branch logic. Returns
 * POSITIVE_INFINITY for an empty line.
 *
 * Inputs: point + vertices in degrees. Output: metres.
 */
function distanceToPolylineMeters(
  point: { lat: number; lng: number },
  line: ReadonlyArray<{ lat: number; lng: number }>,
): number {
  if (line.length === 0) return Number.POSITIVE_INFINITY;
  let min = Number.POSITIVE_INFINITY;
  for (const v of line) {
    const d = haversineMeters(point, v);
    if (d < min) min = d;
  }
  return min;
}

/* ─── Output formatters ────────────────────────────────────────────── */

/** Format a length in metres as `"123 m"` or `"4.56 km"`. */
function formatLength(meters: number): string {
  if (!Number.isFinite(meters) || meters < 0) return 'unknown length';
  if (meters >= M_TO_KM_THRESHOLD) {
    return `${(meters / M_PER_KM).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
}

/** Format an area in m² as `"123 m²"` or `"4.56 ac"`. */
function formatArea(squareMeters: number): string {
  if (!Number.isFinite(squareMeters) || squareMeters < 0) return 'unknown area';
  if (squareMeters >= M2_TO_ACRE_THRESHOLD) {
    return `${(squareMeters / M2_PER_ACRE).toFixed(2)} ac`;
  }
  return `${Math.round(squareMeters)} m²`;
}

/* ─── Category labels and override hints ───────────────────────────── */

const CATEGORY_LABEL: Record<SiteLayerCategory, string> = {
  'infrastructure.pipeline':   'Pipeline (gas/water)',
  'infrastructure.electrical': 'Electrical (transmission/distribution)',
  'infrastructure.fiber':      'Fiber / telecom',
  'infrastructure.transport':  'Rail / road',
  'constraint.environmental':  'Environmental constraint (wetland, floodplain, habitat)',
  'concept.expansion':         'Planned / expansion zone',
  'concept.generation':        'Generation concept area',
  'concept.ccs':               'CCS / compression concept area',
  'impact.noise':              'Noise screening contour',
  'impact.receptor':           'Impact receptor',
  'impact.barrier':            'Acoustic barrier',
  'other':                     'Other',
};

/**
 * Override directives for categories whose on-site presence routinely
 * contradicts a coarse regional dataset. The directive list at the
 * bottom of the block tells the LLM which on-site signals to trust
 * over which regional signal.
 */
const SED_OVERRIDE_HINTS: Partial<Record<SiteLayerCategory, string>> = {
  'infrastructure.fiber':
    'on-site fiber overrides PeeringDB regional nearest-IX distance for last-mile claims',
  'infrastructure.electrical':
    'on-site transmission overrides HIFLD nearest-line distance — interconnection may be feasible at the on-site tap',
  'infrastructure.pipeline':
    'on-site pipeline overrides EIA / state pipeline maps for last-mile gas availability',
  'constraint.environmental':
    'on-site wetland / floodplain layer is parcel-level truth — overrides NWI / FEMA regional shapefiles for THIS site',
  'infrastructure.transport':
    'on-site rail / road overrides regional transport data for site-access claims',
};

/**
 * Stable category render order — infrastructure first (those are the
 * categories most prone to conflicting with the SED), then constraints,
 * then concepts, then other.
 */
const CATEGORY_RENDER_ORDER: SiteLayerCategory[] = [
  'infrastructure.fiber',
  'infrastructure.electrical',
  'infrastructure.pipeline',
  'infrastructure.transport',
  'constraint.environmental',
  'concept.expansion',
  'other',
];

const PLANNING_CATEGORY_RENDER_ORDER: SiteLayerCategory[] = [
  'concept.generation',
  'infrastructure.electrical',
  'infrastructure.pipeline',
  'concept.ccs',
  'concept.expansion',
  'impact.receptor',
  'impact.noise',
];

/**
 * Scenario-derived geometry is a deterministic planning output, not imported
 * parcel truth. Keep it out of the KMZ override tier even for legacy rows that
 * predate the versioned publication attributes.
 */
export function isGenerationImpactPlanningLayer(layer: SiteLayer): boolean {
  return layer.source === 'scenario-derived';
}

/* ─── Public API ───────────────────────────────────────────────────── */

export interface BuildSiteLayersBlockInput {
  siteLayers?: ReadonlyArray<SiteLayer>;
  siteCentroid?: { lat: number; lng: number };
  siteBoundary?: ReadonlyArray<{ lat: number; lng: number }>;
  /**
   * Multi-Parcel Site Boundary (MP pack, MP8) — when the site has
   * multiple parcels, this enumerates them so the layers block can
   * emit per-parcel area in the prompt (instead of treating the site
   * as a single polygon). The siteBoundary input is kept for
   * back-compat; when both are set, parcels[] wins. Single-parcel
   * sites omit this field; behaviour identical to legacy.
   */
  parcels?: ReadonlyArray<import('../types/siteBoundary').SiteBoundaryParcel>;
  /** When set, totalParcelAcres uses union totalArea instead of summing parcels. */
  siteBoundaryAggregate?: import('../types/siteBoundary').SiteBoundaryAggregate;
}

/**
 * Summary stats describing the block that was built — emitted by
 * `buildSiteLayersBlockWithStats` for telemetry. The plain
 * `buildSiteLayersBlock` consumer doesn't need these.
 */
export interface SiteLayersBlockStats {
  /** Total visible layers in the block (after `visible !== false` filter). */
  visibleLayerCount: number;
  /** Visible layers that are allowed to enter a report prompt. */
  reportableLayerCount: number;
  siteTruthLayerCount: number;
  screeningLayerCount: number;
  /** Visible map-only layers intentionally excluded from report evidence. */
  excludedMapOnlyLayerCount: number;
  /** Total layers on the site (visible + hidden). */
  totalLayerCount: number;
  polygonCount: number;
  polylineCount: number;
  pointCount: number;
  /** Rendered block length in chars (post any truncation). */
  blockChars: number;
  /** Unique categories present in the block. */
  categories: SiteLayerCategory[];
  /** True iff the rendered block was truncated to MAX_BLOCK_CHARS. */
  truncated: boolean;
  /**
   * Multi-Parcel Site Boundary (MP pack, MP8) — number of parcels the
   * site is composed of (1 for legacy single-parcel sites). Logged
   * alongside per-layer stats so the LK telemetry line surfaces multi-
   * parcel context.
   */
  parcelCount: number;
  /** MP8 — total acreage across all parcels (acres). */
  totalParcelAcres: number;
}

export function renderLandGateFact(fact: import('../types/site').LandGatePromotedFact): string {
  if (fact.kind === 'parcel') {
    const values = [
      fact.parcelId ? `parcel ID ${fact.parcelId}` : null,
      fact.acreage != null ? `${fact.acreage} ac` : null,
      fact.owner ? `owner ${fact.owner}` : null,
    ].filter(Boolean);
    return `Parcel — ${values.join(', ')}`;
  }
  if (fact.kind === 'zoning') {
    return `Zoning — ${fact.zoningCode}${fact.jurisdiction ? ` (${fact.jurisdiction})` : ''}`;
  }
  if (fact.kind === 'constraint') {
    return `Constraint — ${fact.constraintType}: ${fact.present ? 'present' : 'not indicated'}${fact.detail ? `; ${fact.detail}` : ''}`;
  }
  return `Existing infrastructure — ${fact.infrastructureType}: ${fact.label}`;
}

/**
 * Build the layers block AND telemetry stats describing it. Returns
 * `{ block: '', stats: EMPTY_STATS }` when the site has no visible
 * layers — callers should treat an empty block as a no-op and prepend
 * nothing to the SED text.
 */
export function buildSiteLayersBlockWithStats(
  input: BuildSiteLayersBlockInput,
): { block: string; stats: SiteLayersBlockStats } {
  const allLayers = input.siteLayers ?? [];
  const partition = partitionSiteLayersForEvidence(allLayers);
  const visibleLayerCount = allLayers.length - partition.hidden.length;
  // Raw LandGate geometry and attributes are map-only even after promotion.
  // Promotion exposes only the typed, sanitized fact snapshot below.
  const landGateCandidates = partition.screening.filter(
    layer => layer.source === 'landgate-import',
  );
  const promotedLandGate = validatedReviewedLandGateLayers(landGateCandidates).reviews;
  const excludedLandGateCount = landGateCandidates.length - promotedLandGate.length;
  const screeningLayers = partition.screening.filter(layer => layer.source !== 'landgate-import');
  const reportableLayers = [...partition.siteTruth, ...screeningLayers];
  // Scenario geometry remains `map-only` under the evidence policy so it can
  // never become site truth. Versioned publications are nevertheless useful
  // planning context, so render them in a separately labelled, non-evidentiary
  // tier with frozen publication provenance.
  const planningLayers = partition.mapOnly.filter(isGenerationImpactPlanningLayer);
  const excludedMapOnlyLayers = partition.mapOnly.filter(
    layer => !isGenerationImpactPlanningLayer(layer),
  );
  const renderedLayers = [...reportableLayers, ...planningLayers];

  // Resolve the reference point used for "on-parcel" / "distance from
  // centroid" stats. Order of preference:
  //   1. explicit siteCentroid
  //   2. arithmetic centroid of siteBoundary (when present)
  //   3. null (omits centroid-relative stats; polyline / polygon stats
  //      are still meaningful, just no on-parcel flag for polylines).
  const referencePoint: { lat: number; lng: number } | null =
    input.siteCentroid
      ? { lat: input.siteCentroid.lat, lng: input.siteCentroid.lng }
      : (input.siteBoundary && input.siteBoundary.length > 0
        ? polygonVertexCentroid(input.siteBoundary)
        : null);

  // Per-category bucketing (preserves layer order within a category).
  let polygonCount = 0;
  let polylineCount = 0;
  let pointCount = 0;
  for (const l of renderedLayers) {
    if (l.geometry.type === 'polygon') polygonCount++;
    else if (l.geometry.type === 'line') polylineCount++;
    else if (l.geometry.type === 'point') pointCount++;
  }

  const lines: string[] = [];

  appendEvidenceLayerGroup(lines, partition.siteTruth, referencePoint, 'site-truth', input);
  appendEvidenceLayerGroup(lines, screeningLayers, referencePoint, 'screening', input);

  if (promotedLandGate.length > 0) {
    lines.push('## [LANDGATE — REVIEWED SCREENING] [NON-CITABLE] ALLOWLISTED FACTS');
    lines.push('These typed facts were explicitly reviewed, but remain commercial screening evidence. Do not cite them as authoritative evidence or assign a [Ref] citation. They do not override professional documents or authoritative government records. Raw vendor geometry and attributes are excluded.');
    for (const { review } of promotedLandGate) {
      for (const fact of review.facts) {
        lines.push(`  - ${renderLandGateFact(fact)}`);
      }
    }
    lines.push('');
  }

  appendPlanningLayerGroup(lines, planningLayers, referencePoint);

  let block = lines.join('\n');
  let truncated = false;
  if (block.length > MAX_BLOCK_CHARS) {
    block = block.slice(0, MAX_BLOCK_CHARS) + '\n[... layers block truncated]';
    truncated = true;
  }

  // MP8 — derive parcel count + total acreage. When parcels[] is set,
  // use it directly; otherwise fall back to "1 parcel" for legacy
  // single-boundary sites. The totalParcelAcres is computed from the
  // parcels' planimetric areas (already in acres on
  // SiteBoundaryParcel) so we don't recompute area here.
  const parcelCount = input.parcels && input.parcels.length > 0
    ? input.parcels.length
    : (input.siteBoundary && input.siteBoundary.length >= 3 ? 1 : 0);
  const totalParcelAcres = input.siteBoundaryAggregate?.totalArea != null
    && Number.isFinite(input.siteBoundaryAggregate.totalArea)
    ? input.siteBoundaryAggregate.totalArea
    : input.parcels && input.parcels.length > 0
      ? unionPolygonAreaAcres(input.parcels)
      : 0;

  const stats: SiteLayersBlockStats = {
    visibleLayerCount,
    reportableLayerCount: renderedLayers.length + promotedLandGate.length,
    siteTruthLayerCount: partition.siteTruth.length,
    screeningLayerCount: screeningLayers.length + promotedLandGate.length,
    excludedMapOnlyLayerCount: excludedMapOnlyLayers.length + excludedLandGateCount,
    totalLayerCount: allLayers.length,
    polygonCount,
    polylineCount,
    pointCount,
    blockChars: block.length,
    categories: Array.from(new Set(renderedLayers.map(layer => layer.category))),
    truncated,
    parcelCount,
    totalParcelAcres,
  };
  return { block, stats };
}

function appendPlanningLayerGroup(
  lines: string[],
  layers: SiteLayer[],
  referencePoint: { lat: number; lng: number } | null,
): void {
  if (layers.length === 0) return;

  const first = layers[0];
  const version = first.attributes?.layoutVersion;
  const publicationId = first.attributes?.layoutPublicationId;
  const inputHash = first.attributes?.scenarioInputHash;
  const resultHash = first.attributes?.scenarioResultHash;
  const versionLabel = typeof version === 'number' ? ` v${version}` : '';

  lines.push(`## [PLANNING LAYOUT${versionLabel}] GENERATION & IMPACT SCENARIO GEOMETRY`);
  lines.push(
    `The active Site Map contains ${layers.length} deterministic planning layer`
    + `${layers.length === 1 ? '' : 's'} generated from a saved Generation & Impact scenario. `
    + 'These blocks are planning-grade, are not surveyed or construction CAD, and must not override imported or manually curated site evidence.',
  );
  if (typeof publicationId === 'string' || typeof inputHash === 'string' || typeof resultHash === 'string') {
    lines.push(
      `Publication: ${typeof publicationId === 'string' ? publicationId : 'legacy unversioned'}`
      + `${typeof inputHash === 'string' ? ` · input ${inputHash.slice(0, 8)}` : ''}`
      + `${typeof resultHash === 'string' ? ` · result ${resultHash.slice(0, 8)}` : ''}`,
    );
  }
  lines.push('');

  const byCategory = new Map<SiteLayerCategory, SiteLayer[]>();
  for (const layer of layers) {
    const bucket = byCategory.get(layer.category) ?? [];
    bucket.push(layer);
    byCategory.set(layer.category, bucket);
  }
  const categoryOrder = [
    ...PLANNING_CATEGORY_RENDER_ORDER,
    ...Array.from(byCategory.keys()).filter(
      category => !PLANNING_CATEGORY_RENDER_ORDER.includes(category),
    ),
  ];
  for (const category of categoryOrder) {
    const inCategory = byCategory.get(category);
    if (!inCategory?.length) continue;
    lines.push(`### ${CATEGORY_LABEL[category]} — ${inCategory.length} layer${inCategory.length === 1 ? '' : 's'}`);
    for (const layer of inCategory) {
      const stats = renderLayerStats(layer, referencePoint);
      const description = layer.description ? ` — ${layer.description}` : '';
      lines.push(`  - "${layer.name}" — ${stats}${description}`);
    }
    lines.push('');
  }
}

function appendEvidenceLayerGroup(
  lines: string[],
  layers: SiteLayer[],
  referencePoint: { lat: number; lng: number } | null,
  use: 'site-truth' | 'screening',
  input: BuildSiteLayersBlockInput,
): void {
  if (layers.length === 0) return;

  const siteTruth = use === 'site-truth';
  lines.push(siteTruth
    ? '## [SITE LAYERS — SITE TRUTH] REVIEWED ON-SITE LAYERS'
    : '## [SITE LAYERS — SCREENING] VERIFY BEFORE RELYING');
  lines.push(siteTruth
    ? 'These parcel-specific user-curated layers may override lower-tier regional screening. Cite the layer name and source.'
    : 'These public or model-derived layers are planning screens. They may fill a gap, but must not override verified provider or professional evidence.');
  lines.push('');

  if (siteTruth && input.parcels && input.parcels.length > 1) {
    lines.push(`SITE PARCEL BREAKDOWN (${input.parcels.length} parcels):`);
    for (let i = 0; i < input.parcels.length; i++) {
      const parcel = input.parcels[i];
      const label = parcel.label ?? `Parcel ${i + 1}`;
      const usePart = parcel.intendedUse ? ` (${parcel.intendedUse})` : '';
      lines.push(`  - ${label}${usePart}: ${parcel.area.toFixed(0)} ac`);
    }
    lines.push('');
  }

  const byCategory = new Map<SiteLayerCategory, SiteLayer[]>();
  for (const layer of layers) {
    const bucket = byCategory.get(layer.category) ?? [];
    bucket.push(layer);
    byCategory.set(layer.category, bucket);
  }

  const overrideHints: string[] = [];
  for (const category of CATEGORY_RENDER_ORDER) {
    const inCategory = byCategory.get(category);
    if (!inCategory?.length) continue;
    lines.push(`### ${CATEGORY_LABEL[category]} — ${inCategory.length} layer${inCategory.length === 1 ? '' : 's'}`);
    for (const layer of inCategory) {
      const stats = renderLayerStats(layer, referencePoint);
      const description = siteTruth && layer.description ? ` — ${layer.description}` : '';
      lines.push(`  - "${layer.name}" — ${stats}${description}`);
    }
    const hint = siteTruth ? SED_OVERRIDE_HINTS[category] : undefined;
    if (hint) overrideHints.push(`  - ${CATEGORY_LABEL[category]}: ${hint}`);
    lines.push('');
  }

  if (overrideHints.length > 0) {
    lines.push('### SITE-TRUTH OVERRIDE DIRECTIVES');
    lines.push(...overrideHints);
    lines.push('Use an override only for the same feature and location; retain conflicting evidence as a disclosed conflict.');
    lines.push('');
  }
}

/**
 * Render the KMZ-imported layers block. Returns `''` when there are no
 * visible layers — callers should treat that as "no banner, no
 * prepended block".
 *
 * This is the primary entry point. Use `buildSiteLayersBlockWithStats`
 * when you also need the stats for telemetry.
 */
export function buildSiteLayersBlock(input: BuildSiteLayersBlockInput): string {
  return buildSiteLayersBlockWithStats(input).block;
}

/* ─── Internal: per-layer stats rendering ──────────────────────────── */

/**
 * Render a one-line stats summary for a single layer. The shape varies
 * by geometry type so the LLM can quickly distinguish a long
 * transmission line (km) from a small wetland (acres) from a single
 * pole (point + distance).
 *
 * Polygons → `polygon, area X ac, perimeter Y km`
 *            (or `m²` / `m` below the crossover thresholds)
 * Lines    → `polyline, length X km[, crosses or abuts the parcel]`
 * Points   → `point, X km from site centroid` (or just `point` if no
 *            reference centroid is available)
 */
function renderLayerStats(
  layer: SiteLayer,
  referencePoint: { lat: number; lng: number } | null,
): string {
  const { geometry } = layer;

  if (geometry.type === 'line') {
    const lenM = polylineLengthMeters(geometry.coordinates);
    const lenStr = formatLength(lenM);
    const onParcel =
      referencePoint !== null &&
      distanceToPolylineMeters(referencePoint, geometry.coordinates) <= ON_PARCEL_TOLERANCE_M;
    return `polyline, length ${lenStr}${onParcel ? ', crosses or abuts the parcel' : ''}`;
  }

  if (geometry.type === 'polygon') {
    const areaM2 = polygonAreaSquareMeters(geometry.coordinates);
    const perimM = polygonPerimeterMeters(geometry.coordinates);
    const polyCentroid = polygonVertexCentroid(geometry.coordinates);
    const centroidStr =
      polyCentroid !== null
        ? `, centroid ${polyCentroid.lat.toFixed(4)},${polyCentroid.lng.toFixed(4)}`
        : '';
    return `polygon, area ${formatArea(areaM2)}, perimeter ${formatLength(perimM)}${centroidStr}`;
  }

  // Point
  const pt = geometry.coordinates[0];
  if (pt && referencePoint !== null) {
    const dM = haversineMeters(referencePoint, pt);
    return `point, ${formatLength(dM)} from site centroid`;
  }
  return 'point';
}
